























































































import { Component, Emit, Prop, Watch, Vue } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import { formatDate, getMonthName, getDateByString } from "@/lib/date";
import XButton from "../SimpleButton.vue";
import { datePickerForm } from "@/assets/scripts/form-fields/main";

const { values, attrs, validations, errors } = datePickerForm;

@Component({
  components: { XButton },
  mixins: [validationMixin],
  computed: {
    ...errors,
    getMin() {
      return this.min;
    },
    getMax() {
      return this.max;
    },
  },
  validations,
})
class DropdownWithDatePicker extends Vue {
  [x: string]: any;

  @Prop({ default: "" }) readonly modClass!: string;
  @Prop({ default: true }) readonly showMdiChevronDown!: boolean;
  @Prop({ default: false }) readonly setDefaultDate!: boolean;

  @Prop({ default: "" }) min!: string;
  @Prop({ default: "" }) max!: string;

  values = values;
  allFieldAttrs = attrs;

  activatorText = "";
  dates: string[] = [];
  currentDateIndex = 0;

  public created() {
    this.defaultStateHook();
    this.emitPickEvt();
  }

  @Watch("setDefaultDate")
  public setDefaultDateChanged() {
    if (this.setDefaultDate) {
      this.resetDateChanges();
    }
  }

  @Emit()
  private emitPickEvt() {
    this.$emit(
      "update:dates",
      this.dates.map((date) => new Date(date).toISOString().slice(0, 19))
    );
  }

  @Emit("click")
  public propagateCustomEvt(evtName: string) {
    this.$emit(evtName, evtName);
  }

  private defaultStateHook() {
    // для даты "от" необходимо устанавливать первое число, поскольку
    // иначе будет выбрано текущее число стартового месяца, а если стартовый
    // месяц февраль, а текущее число больше чем число дней в феврале в текущем году,
    // то стартовый месяц станет мартом, а его число сдвинется на кол-во дней,
    // на которое число текущего месяца превышает общее кол-во дней в феврале текущего года
    const startDate = new Date((new Date()).setDate(1));
    const endDate = new Date();

    // Использовать значение 5, поскольку индексация месяцев начинается с 0,
    // что позволяет установить дате значение, которое отличается от текущего
    // на полгода.
    startDate.setMonth(startDate.getMonth() - 5);

    // Применяем ограничения min/max к датам по умолчанию
    let finalStartDate = startDate;
    let finalEndDate = endDate;

    if (this.min) {
      const minDate = new Date(this.min);
      if (minDate && finalStartDate < minDate)
        finalStartDate = new Date(minDate);
    }

    if (this.max) {
      const maxDate = new Date(this.max);
      if (maxDate && finalEndDate > maxDate)
        finalEndDate = new Date(maxDate);
    }

    // Убеждаемся, что startDate не больше endDate
    if (finalStartDate > finalEndDate) {
      finalStartDate = new Date(finalEndDate);
      finalStartDate.setMonth(finalEndDate.getMonth() - 1);
    }

    const humanDates = [ finalStartDate, finalEndDate ].map((date) =>
      formatDate(date, "monthAndYear")
    );
    const isoDates = [ finalStartDate, finalEndDate ].map((date) =>
      date.toISOString().slice(0, 7)
    );

    this.values.startDate = humanDates[0];
    this.values.endDate = humanDates[1];
    this.dates = isoDates;

    this.activatorText = humanDates.join(" - ");
  }

  public formatMonth(date: string): string {
    return getMonthName(date, true).toUpperCase();
  }

  public onMonthClick(date: string) {
    const formattedDate = formatDate(date, "monthAndYear");

    if (this.currentDateIndex) {
      this.values.endDate = formattedDate;
      this.currentDateIndex--;
    } else {
      this.values.startDate = formattedDate;
      this.currentDateIndex++;
    }
  }

  public applyDateChanges() {
    this.validateAndCorrectDate('startDate');
    this.validateAndCorrectDate('endDate');

    if (this.$v.$invalid)
      return;

    const { startDate, endDate } = this.values;

    const isoDates = [startDate, endDate].map((date) => getDateByString(date));
    const parsedDates = isoDates.map((date) => Date.parse(date.toString()));

    if (parsedDates[0] > parsedDates[1]) {
      this.dates = isoDates.reverse();
      this.values.startDate = endDate;
      this.values.endDate = startDate;
    } else {
      this.dates = isoDates;
    }

    this.currentDateIndex = 0;
    this.activatorText = Object.values(this.values).join(" - ");

    this.emitPickEvt();
  }

  public resetDateChanges() {
    this.defaultStateHook();

    // Проверяем, что сброшенные даты соответствуют min/max
    const startDate = this.parseDateFromMask(this.values.startDate);
    const endDate = this.parseDateFromMask(this.values.endDate);

    if (startDate && !this.isDateInRange(startDate)) {
      const constrained = this.constrainDate(startDate);
      if (constrained)
        this.values.startDate = this.formatDateToMask(constrained);
    }

    if (endDate && !this.isDateInRange(endDate)) {
      const constrained = this.constrainDate(endDate);
      if (constrained)
        this.values.endDate = this.formatDateToMask(constrained);
    }

    this.syncDatesFromFields();
    this.$v.$reset();
    this.currentDateIndex = 0;
  }

  public getErrors(fieldName: string): string | string[] {
    return this[`${fieldName}Errors`];
  }

  // Преобразование формата MM.YYYY в Date объект
  private parseDateFromMask(value: string): Date | null {
    if (!value || !value.match(/^\d{2}\.\d{4}$/)) return null;
    const [ month, year ] = value.split('.');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return isNaN(date.getTime()) ? null : date;
  }

  // Преобразование Date в формат MM.YYYY
  private formatDateToMask(date: Date): string {
    if (!date || isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}.${year}`;
  }

  // Проверка, находится ли дата в допустимых пределах
  private isDateInRange(date: Date | null): boolean {
    if (!date)
      return true;

    const minDate = this.min ? new Date(this.min) : null;
    const maxDate = this.max ? new Date(this.max) : null;

    if (minDate && minDate instanceof Date && !isNaN(minDate.getTime())) {

      // Сравниваем только год и месяц
      const dateMonth = date.getFullYear() * 12 + date.getMonth();
      const minMonth = minDate.getFullYear() * 12 + minDate.getMonth();

      if (dateMonth < minMonth)
        return false;
    }

    if (maxDate && maxDate instanceof Date && !isNaN(maxDate.getTime())) {
      const dateMonth = date.getFullYear() * 12 + date.getMonth();
      const maxMonth = maxDate.getFullYear() * 12 + maxDate.getMonth();

      if (dateMonth > maxMonth)
        return false;
    }

    return true;
  }

  // Корректировка даты по min/max
  private constrainDate(date: Date | null): Date | null {
    if (!date)
      return date;

    const minDate = this.min ? new Date(this.min) : null;
    const maxDate = this.max ? new Date(this.max) : null;

    let result = new Date(date);
    const resultMonth = result.getFullYear() * 12 + result.getMonth();

    if (minDate && minDate instanceof Date && !isNaN(minDate.getTime())) {
      const minMonth = minDate.getFullYear() * 12 + minDate.getMonth();
      if (resultMonth < minMonth)
        result = new Date(minDate);
    }

    if (maxDate && maxDate instanceof Date && !isNaN(maxDate.getTime())) {
      const maxMonth = maxDate.getFullYear() * 12 + maxDate.getMonth();
      if (result.getFullYear() * 12 + result.getMonth() > maxMonth)
        result = new Date(maxDate);
    }

    return result;
  }

  // Валидация и коррекция ручного ввода
  private validateAndCorrectDate(fieldName: string) {
    const value = this.values[fieldName];
    const date = this.parseDateFromMask(value);

    if (date && !this.isDateInRange(date)) {
      const constrained = this.constrainDate(date);
      if (constrained) {
        this.values[fieldName] = this.formatDateToMask(constrained);

        // Обновляем dates для синхронизации с календарем
        this.syncDatesFromFields();
      }
    } else if (date) {
      this.syncDatesFromFields();
    }

    this.$v.values[fieldName].$touch();
  }

  // Синхронизация dates из текстовых полей
  private syncDatesFromFields() {
    const startDate = this.values.startDate ? this.parseDateFromMask(this.values.startDate) : null;
    const endDate = this.values.endDate ? this.parseDateFromMask(this.values.endDate) : null;

    const isoDates = [ startDate, endDate ]
        .map(date => date ? date.toISOString().slice(0, 7) : '')
        .filter(d => d);

    if (isoDates.length === 2)
      this.dates = isoDates;
  }
}

export default DropdownWithDatePicker;
